import { useState } from "react";
import { ChevronDown, HelpCircle } from "lucide-react";

import { Card, CardContent } from "@/components/ui/card";

/**
 * O que o agente faz, em português, na página onde ele mora.
 *
 * POR QUE existe: quase tudo aqui é descobrível só se a pessoa já souber que
 * existe. O envio de e-mail é o caso extremo — ele funciona por pedido em
 * linguagem natural ("me manda todo dia às 8"), e pedido em linguagem natural é
 * invisível numa tela de botões. Sem este texto, a função existe e ninguém usa.
 *
 * Fica FECHADO por padrão: quem já sabe não precisa rolar por cima dele todo
 * dia. Aberto uma vez, é o suficiente.
 *
 * CUIDADO AO EDITAR: os limites citados aqui (10 por dia, 1 por destinatário)
 * são os da migration 105; a conversa acontece na North IA (chat-ai), que
 * desde a fusão de 24/09/2026 é o chat único e usa as instruções pessoais e as
 * capacidades ligadas aqui (chat-ai/permissoes.ts). Texto que promete o que o
 * código não faz é pior do que texto nenhum — se mudar lá, mude aqui.
 */

const EXEMPLOS = [
  "Quem eu preciso contatar hoje?",
  "Envie por e-mail, todos os dias às 8h, os clientes inadimplentes.",
  "Quanto falta para atingir meu objetivo do mês?",
  "Escreva um e-mail para o João sobre a assembleia do dia 20.",
  "Quais vendas foram confirmadas ou canceladas nesta semana?",
];

export const GuiaDoAgente = () => {
  const [aberto, setAberto] = useState(false);

  return (
    <Card>
      <button
        type="button"
        onClick={() => setAberto((v) => !v)}
        className="flex w-full items-center gap-2 px-6 py-4 text-left"
      >
        <HelpCircle className="h-4 w-4 text-primary" />
        <span className="flex-1 text-sm font-medium">Funcionalidades do agente</span>
        <ChevronDown
          className={`h-4 w-4 text-muted-foreground transition-transform ${aberto ? "rotate-180" : ""}`}
        />
      </button>

      {aberto && (
        <CardContent className="space-y-5 border-t pt-5 text-sm">
          <Bloco titulo="Briefing diário">
            Todo dia útil pela manhã, o agente gera um briefing com as mudanças desde o dia
            anterior e as pendências do dia: contatos, vencimentos, vendas e cancelamentos. O
            briefing é exibido no card do agente e sai do menu após a leitura.
          </Bloco>

          <Bloco titulo="Conversa na North IA">
            O botão <strong>Conversar</strong> abre a <strong>North IA</strong>. Lá o agente
            consulta a carteira (leads, vendas, cobrança, objetivo do mês, campanhas vigentes) e
            monta negócios: escolhe grupo, simula, compara e gera a proposta em PDF.
            <ul className="mt-2 space-y-1">
              {EXEMPLOS.map((e) => (
                <li key={e} className="text-muted-foreground">
                  · “{e}”
                </li>
              ))}
            </ul>
          </Bloco>

          <Bloco titulo="Envio de e-mail">
            <p>
              O agente redige o e-mail solicitado na conversa, e o envio ocorre após
              confirmação. Destinatários permitidos: <strong>o próprio usuário</strong> ou um{" "}
              <strong>cliente da carteira</strong>. Outros endereços são recusados.
            </p>
            <p className="mt-2">
              Para envio recorrente, registre o pedido em{" "}
              <strong>Editar → “Informações sobre você”</strong>. Exemplo:{" "}
              <em>“enviar diariamente por e-mail os clientes inadimplentes”</em>. O briefing da
              manhã passa a ser enviado também por e-mail.
            </p>
            <p className="mt-2 text-xs text-muted-foreground">
              Limites de segurança: até 10 e-mails por dia e 1 por dia para o mesmo
              destinatário. Envios bloqueados são informados no briefing, com o motivo.
            </p>
          </Bloco>

          <Bloco titulo="Chat unificado">
            As conversas com o agente acontecem na North IA, que mantém as 30 conversas mais
            recentes. As instruções e capacidades configuradas aqui valem também na North IA.
          </Bloco>

          <Bloco titulo="Configuração">
            Em <strong>Editar</strong> é possível ativar ou desativar capacidades, alterar o
            horário e os dias do briefing e informar o contexto de trabalho, aplicado a
            todas as conversas na North IA. <strong>Pausar</strong> interrompe o briefing sem
            apagar dados.
          </Bloco>
        </CardContent>
      )}
    </Card>
  );
};

const Bloco = ({ titulo, children }: { titulo: string; children: React.ReactNode }) => (
  <div>
    <p className="mb-1 font-medium">{titulo}</p>
    <div className="text-muted-foreground">{children}</div>
  </div>
);
